import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import { notify } from '@/components/Notice/Notice';
import { TRANSACTIONS_COPY as COPY } from '@/features/reports/copy';
import { saveFile } from '@/lib/download';

/**
 * Export, as the prototype's report cards carry it: the table as an Excel file the API builds
 * from the same figures as the screen, so the file holds what the reader is looking at. The
 * label says Exported for a moment once the file is handed over, as the prototype's does. A
 * failed export says so and leaves the label alone: there is no tab to fall back to, since the
 * file is built on request and opening the URL past the `await` would be blocked as a pop-up.
 */
export function ExportButton({
    disabled,
    ...file
}: (
    | { url: string; fileName: string }
    /** A file the screen writes itself, such as App usage's session log as a CSV (ALS-160). */
    | { save: () => Promise<void> }
) & { disabled?: boolean }) {
    const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');

    useEffect(() => {
        if (state !== 'done') return;
        const reset = window.setTimeout(() => setState('idle'), 1400);
        return () => window.clearTimeout(reset);
    }, [state]);

    async function run() {
        setState('busy');
        try {
            await ('save' in file ? file.save() : saveFile(file.url, file.fileName));
            setState('done');
        } catch {
            notify.error(COPY.exportFailed);
            setState('idle');
        }
    }

    return (
        <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            loading={state === 'busy'}
            onClick={() => void run()}
        >
            {state === 'busy' ? null : <Download />}
            {state === 'done' ? COPY.exported : COPY.export}
        </Button>
    );
}
